import { Card, Tag, Typography, Descriptions, Empty, Divider } from "antd";
import {
  GlobalOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

interface Props {
  tenant: any;
}

export const TenantDetailsCard: React.FC<Props> = ({ tenant }) => {
  const posClients: any[] = Array.isArray(tenant.pos_client)
    ? tenant.pos_client
    : [];

  return (
    <Card className="rounded-2xl border-gray-100 shadow-sm overflow-hidden h-fit transition-all duration-300 hover:shadow-md">
      <div className="flex items-center justify-between mb-4">
        <div>
          <Title level={4} className="!mb-1 text-gray-800">
            {tenant.name}
          </Title>
          <Text type="secondary" className="text-xs">
            Tenant ID: {tenant.id?.slice(0, 8)}...
          </Text>
        </div>
        <Tag color={tenant.status === "active" ? "success" : "error"} className="rounded-full px-3 py-0.5 text-xs font-medium border-0">
          {tenant.status}
        </Tag>
      </div>

      <Descriptions column={1} size="small" className="mb-6 bg-gray-50/50 p-4 rounded-xl border border-gray-100/50">
        <Descriptions.Item
          label={
            <span className="inline-flex items-center gap-1.5 text-gray-500 font-medium text-xs">
              <GlobalOutlined className="text-gray-400" /> Sub Name
            </span>
          }
        >
          <span className="text-[12px] text-gray-700 font-semibold">
            {tenant.tenant_sub_name}
          </span>
        </Descriptions.Item>

        <Descriptions.Item
          label={<span className="text-gray-500 font-medium text-xs">Created</span>}
        >
          <span className="text-[12px] text-gray-700 font-semibold">
            {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : "-"}
          </span>
        </Descriptions.Item>

        <Descriptions.Item
          label={<span className="text-gray-500 font-medium text-xs">Updated</span>}
        >
          <span className="text-[12px] text-gray-700 font-semibold">
            {tenant.updated_at ? new Date(tenant.updated_at).toLocaleDateString() : "-"}
          </span>
        </Descriptions.Item>
      </Descriptions>

      <div>
        <div className="flex items-center gap-1.5 mb-3">
          <UserOutlined className="text-gray-400" />
          <Text strong className="text-xs text-gray-600 uppercase tracking-wide">
            POS Clients
          </Text>
        </div>

        {posClients.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No POS clients found"
            className="!my-2"
          />
        ) : (
          <div className="space-y-3">
            {posClients.map((client) => (
              <div
                key={client.id}
                className="rounded-xl border border-gray-100 bg-white p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-800">
                    {client.client_name || "-"}
                  </span>
                  <Tag
                    color={client.status === "active" ? "green" : "default"}
                    className="!rounded-full !px-2 !py-0 !text-[10px] !border-0"
                  >
                    {client.status}
                  </Tag>
                </div>
                <div className="space-y-1 text-[12px] text-gray-600">
                  {client.contact_number && (
                    <div className="flex items-center gap-1.5">
                      <PhoneOutlined className="text-gray-400" />
                      {client.contact_number}
                    </div>
                  )}
                  {client.email && (
                    <div className="flex items-center gap-1.5">
                      <MailOutlined className="text-gray-400" />
                      {client.email}
                    </div>
                  )}
                  {client.address && (
                    <div className="flex items-start gap-1.5">
                      <EnvironmentOutlined className="text-gray-400 mt-0.5" />
                      <span>{client.address}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
